import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import Highcharts, { SeriesOptionsType } from 'highcharts/highstock';

import { ReduxState } from '@/redux/modules/types';
import { useGetAvailableScenariosQuery } from '@/redux/api';

import LocalCurrency from '@/types/local-currency';

// Selectors
import { selectActiveComparison, selectSelectedHistoricalScenarios, selectActiveComparisonItems } from '@/redux/modules/comparison/selectors';

import { getItemSeriesData, getSeriesOptionsType } from '@/utils/comparison/get-series-data';
import { deepClone } from '@/utils/objects';
import { ComparisonItemForHistoricalScenario } from '@/types/calculated-historical-scenario';
import LocalizedString from '@/types/localized-string';
import { useHistoricalScenarioAPIData } from './useHistoricalScenarioAPIData';
import { formatItemsForHistoricalScenarios } from '../../Charts/utils/drawdown-chart-helpers';
import { formatRiskRewardItemsForHistoricalScenarios } from '../../Charts/utils/risk-reward-chart-helpers';

export const useHistoricalScenariosState = () => {
  const { data } = useGetAvailableScenariosQuery();
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const availableScenarios = data?.availableScenarios || [];

  const localCurrency = useSelector((state: ReduxState) => state.auth.user?.appConfig?.localCurrency || LocalCurrency.CAD);
  const locale = useSelector((state: ReduxState) => state.intl.locale);
  const user = useSelector((state: ReduxState) => state.auth.user);
  const activeComparison = useSelector(selectActiveComparison);
  const selectedHistoricalScenarios = useSelector(selectSelectedHistoricalScenarios);
  const comparisonItems = useSelector(selectActiveComparisonItems);

  const showBaseCurrencies = activeComparison?.showBaseCurrencies || false;
  const capital = activeComparison?.options?.growth?.capital || 100000;

  const [seriesData, setSeriesData] = useState<Highcharts.SeriesOptionsType[]>([]);
  const [drawdownData, setDrawdownData] = useState<any[]>([]);
  const [scatterPlotData, setScatterPlotData] = useState<any[]>([]);

  const { calculatedHistoricalScenarios, isFetching } = useHistoricalScenarioAPIData();

  const isLoading = Boolean(isFetching || isCalculating);

  useEffect(() => {
    if (calculatedHistoricalScenarios && selectedHistoricalScenarios && calculatedHistoricalScenarios.length > 0 && selectedHistoricalScenarios.length > 0) {
      setIsCalculating(true);

      try {
        let newSeriesData: SeriesOptionsType[] = [];

        const items: (ComparisonItemForHistoricalScenario & { color?: string; name: LocalizedString; type: string })[] =
          calculatedHistoricalScenarios[0].items.map(item => {
            const activeItem = activeComparison?.items.find(i => i._id === item._id);
            return {
              ...deepClone(item),
              name: activeItem!.name,
              type: activeItem!.type,
            };
          });

        // Extract scenario metrics from the first calculated scenario
        const scenarioMetricsData = calculatedHistoricalScenarios[0]?.scenarioMetrics || [];

        for (const item of items) {
          const comparisonItem = activeComparison?.items.find(i => i._id === item._id);
          if (!comparisonItem) continue;
          item.color = comparisonItem.color;
          const commonProps: SeriesOptionsType = getSeriesOptionsType(
            item,
            showBaseCurrencies ? 'BASE' : comparisonItem.currency || localCurrency,
            showBaseCurrencies ? comparisonItem.currency : localCurrency,
          );
          const data = getItemSeriesData(
            item.rolling,
            commonProps,
            item,
            !!item.rollingBackfillTimestamp,
            item.rollingBackfillTimestamp,
            showBaseCurrencies,
            showBaseCurrencies,
            capital,
          );
          const scenarioMetric = scenarioMetricsData.find(metric => metric._id === item._id);
          const dataWithMetrics = data.map(point => ({
            ...point,
            scenarioMetrics: scenarioMetric,
          }));
          newSeriesData = [...newSeriesData, ...dataWithMetrics];
        }

        const drawdownData = formatItemsForHistoricalScenarios({
          data: items.map(item => {
            const activeItem = activeComparison?.items.find(i => i._id === item._id);
            return {
              ...item,
              currency: activeItem!.currency,
              isFX: activeItem!.isFX,
              type: activeItem!.type,
              isSubHolding: false,
              name: activeItem!.name,
              color: item.color,
            };
          }),
          showBaseCurrencies: activeComparison!.showBaseCurrencies,
          localCurrency: user?.appConfig.localCurrency,
          fillArea: true,
        });

        const scatterPlotData = formatRiskRewardItemsForHistoricalScenarios({
          chartItems: items.map(item => {
            const activeItem = activeComparison?.items.find(i => i._id === item._id);
            return {
              ...item,
              currency: activeItem!.currency,
              isFX: activeItem!.isFX,
              type: activeItem!.type,
              isSubHolding: false,
              name: activeItem!.name,
              color: item.color,
            };
          }),
          showBaseCurrencies: activeComparison!.showBaseCurrencies,
          localCurrency: user?.appConfig.localCurrency || '',
        });

        setSeriesData(newSeriesData);
        setDrawdownData(drawdownData);
        setScatterPlotData(scatterPlotData);
      } finally {
        setIsCalculating(false);
      }
    } else {
      setSeriesData([]);
      setDrawdownData([]);
      setScatterPlotData([]);
      setIsCalculating(false);
    }
  }, [comparisonItems, capital, showBaseCurrencies, localCurrency, calculatedHistoricalScenarios]);

  return {
    activeComparison,
    availableScenarios,
    comparisonItems,
    capital,
    drawdownData,
    scatterPlotData,
    localCurrency,
    locale,
    selectedHistoricalScenarios,
    showBaseCurrencies,
    seriesData,
    user,
    isLoading,
  };
};
